import {isEqual} from 'lodash';
import {
  createParser,
  createSerializer,
  parseAsBoolean,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from 'nuqs';
import {useMemo} from 'react';

import {DEFAULT_CONFIGURATIONS} from '@src/api/configurations';
import {
  INTERPOLATOR,
  InterpolatorValue,
  LAST_CONFIGURATION_ID_LOCAL_STORAGE_KEY,
  MOSAICKING_ORDER,
  MosaickingOrderValue,
} from '@src/constants/analyze';

export type RangeTuple = [number, number];

type SuperResParams = Partial<{
  superRes: boolean | null;
  confidenceThreshold?: number | null;
  superResOpacity?: number | null;
}>;

export const SETTINGS_URL_KEYS_MAP = {
  mosaickingOrder: 'mosaicking',
  upsampling: 'upsampling',
  downsampling: 'downsampling',
};

export const EFFECTS_URL_KEYS_MAP = {
  gain: 'gain',
  gamma: 'gamma',
  redRange: 'rr',
  greenRange: 'rg',
  blueRange: 'rb',
} as const;

export const SUPER_RES_URL_KEYS_MAP = {
  superRes: 'super-res',
  confidenceThreshold: 'confidence-threshold',
  superResOpacity: 'opacity',
} as const;

export const URL_KEY_MAP = {
  configurationId: 'configuration',
  collectionId: 'collection',
  layerId: 'layer',
  ...SETTINGS_URL_KEYS_MAP,
  ...EFFECTS_URL_KEYS_MAP,
  ...SUPER_RES_URL_KEYS_MAP,
} as const;

type SettingsProps = {
  mosaickingOrder?: MosaickingOrderValue;
  upsampling?: InterpolatorValue;
  downsampling?: InterpolatorValue;
};

type EffectsProps = SettingsProps & {
  gain?: number;
  gamma?: number;
  redRange?: RangeTuple;
  greenRange?: RangeTuple;
  blueRange?: RangeTuple;
};

type VisualizationParams = {
  configurationId: string;
  collectionId: string | null;
  layerId: string | null;
  isSuperRes: boolean;
  confidenceThreshold: number | null;
  showConfidence: boolean;
  superResOpacity: number | null;
  mosaickingOrder: MosaickingOrderValue;
  upsampling: InterpolatorValue;
  downsampling: InterpolatorValue;
  gain: number;
  gamma: number;
  redRange: RangeTuple;
  greenRange: RangeTuple;
  blueRange: RangeTuple;
  settingsEffectsApplies: boolean;
  serialize: typeof serialize;
  setConfigurationId: (id: string) => void;
  setCollectionId: (
    collectionId: string | null,
    layerId: string | null,
  ) => void;
  setLayerId: (id: string | null) => void;
  setSuperResParams: (params: SuperResParams | null) => void;
  setParams: (props: EffectsProps) => void;
  resetVisualizationParams: () => void;
};

export const SETTINGS_EFFECTS_DEFAULTS: Required<EffectsProps> = {
  mosaickingOrder: MOSAICKING_ORDER.mostRecent,
  upsampling: INTERPOLATOR.bilinear,
  downsampling: INTERPOLATOR.bilinear,
  gain: 1,
  gamma: 1,
  redRange: [0, 1],
  greenRange: [0, 1],
  blueRange: [0, 1],
} as const;

export const SUPER_RES_DEFAULTS: Required<SuperResParams> = {
  superRes: false,
  confidenceThreshold: null,
  superResOpacity: 1,
} as const;

const isValidFloat = (value: string, min: number, max: number) => {
  const floatValue = parseFloat(value);
  const isValid =
    Number.isFinite(floatValue) && floatValue >= min && floatValue <= max;
  return isValid ? floatValue : null;
};

const createParseAsEffect = (min: number, max: number) =>
  createParser({
    parse(queryValue) {
      return isValidFloat(queryValue, min, max);
    },
    serialize(value) {
      return value.toFixed(2);
    },
  });

const createParseAsFloatRange = () =>
  createParser({
    parse(queryValue): RangeTuple | null {
      const values = queryValue.split('/');
      if (values.length !== 2) {
        return null;
      }
      const parsedValues = values
        .map(v => isValidFloat(v, 0, 1))
        .filter(Boolean);

      return parsedValues.length === 2 ? (parsedValues as RangeTuple) : null;
    },
    serialize(value) {
      return `${value[0]?.toFixed(2)}/${value[1]?.toFixed(2)}`;
    },
  });

const getDefaultConfigurationId = () =>
  window.localStorage.getItem(LAST_CONFIGURATION_ID_LOCAL_STORAGE_KEY) ||
  DEFAULT_CONFIGURATIONS[0].id;

const getDefaultSerializerParams = () => {
  return [
    {
      configurationId: parseAsString.withDefault(getDefaultConfigurationId()),
      collectionId: parseAsString,
      layerId: parseAsString,
      superRes: parseAsBoolean.withDefault(false),
      confidenceThreshold: createParseAsEffect(0, 1),
      superResOpacity: createParseAsEffect(0, 1),
      mosaickingOrder: parseAsStringEnum<MosaickingOrderValue>(
        Object.values(MOSAICKING_ORDER),
      ),
      upsampling: parseAsStringEnum<InterpolatorValue>(
        Object.values(INTERPOLATOR),
      ),
      downsampling: parseAsStringEnum<InterpolatorValue>(
        Object.values(INTERPOLATOR),
      ),
      gain: createParseAsEffect(0, 10),
      gamma: createParseAsEffect(0, 10),
      redRange: createParseAsFloatRange(),
      greenRange: createParseAsFloatRange(),
      blueRange: createParseAsFloatRange(),
    },
    {
      urlKeys: URL_KEY_MAP,
      clearOnDefault: false,
    },
  ] as const;
};

export const serialize = createSerializer(...getDefaultSerializerParams());

const useVisualizationParams = (): VisualizationParams => {
  const [
    {
      configurationId,
      collectionId,
      layerId,
      mosaickingOrder,
      upsampling,
      downsampling,
      gain,
      gamma,
      redRange,
      greenRange,
      blueRange,
      superRes,
      confidenceThreshold,
      superResOpacity,
    },
    setVisualizationParams,
  ] = useQueryStates(...getDefaultSerializerParams());

  const effectsProps = {
    mosaickingOrder,
    upsampling,
    downsampling,
    gain,
    gamma,
    redRange,
    greenRange,
    blueRange,
  };

  const settingsEffectsApplies = Object.entries(effectsProps)
    .filter(([, value]) => value !== null)
    .some(
      ([key, value]) =>
        !isEqual(
          value,
          SETTINGS_EFFECTS_DEFAULTS[
            key as keyof typeof SETTINGS_EFFECTS_DEFAULTS
          ],
        ),
    );

  const setters = useMemo(
    () => ({
      setConfigurationId: (id: string) => {
        return setVisualizationParams(() => ({
          configurationId: id,
          collectionId: null,
          layerId: null,
        }));
      },
      setCollectionId: (
        collectionId: string | null,
        layerId: string | null,
      ) => {
        return setVisualizationParams(prev => ({
          ...prev,
          collectionId,
          layerId,
        }));
      },
      setLayerId: (id: string | null) => {
        return setVisualizationParams(prev => ({
          ...prev,
          layerId: id,
        }));
      },
      setSuperResParams: (params: SuperResParams | null) => {
        return setVisualizationParams(prev => ({
          ...prev,
          ...(params
            ? params
            : {
                superRes: null,
                confidenceThreshold: null,
                superResOpacity: null,
              }),
        }));
      },
      setParams: (props: EffectsProps) => {
        return setVisualizationParams(prev => ({
          ...prev,
          ...props,
        }));
      },
      resetVisualizationParams: () => {
        return setVisualizationParams({
          configurationId: getDefaultConfigurationId(),
          collectionId: null,
          layerId: null,
          mosaickingOrder: SETTINGS_EFFECTS_DEFAULTS.mosaickingOrder,
          gain: SETTINGS_EFFECTS_DEFAULTS.gain,
          gamma: SETTINGS_EFFECTS_DEFAULTS.gamma,
          redRange: SETTINGS_EFFECTS_DEFAULTS.redRange,
          blueRange: SETTINGS_EFFECTS_DEFAULTS.blueRange,
          greenRange: SETTINGS_EFFECTS_DEFAULTS.greenRange,
          upsampling: INTERPOLATOR.bilinear,
          downsampling: INTERPOLATOR.bilinear,
          superRes: SUPER_RES_DEFAULTS.superRes,
          confidenceThreshold: SUPER_RES_DEFAULTS.confidenceThreshold,
          superResOpacity: SUPER_RES_DEFAULTS.superResOpacity,
        });
      },
    }),
    [setVisualizationParams],
  );
  return {
    configurationId,
    collectionId,
    layerId,
    isSuperRes: superRes,
    confidenceThreshold:
      confidenceThreshold ?? SUPER_RES_DEFAULTS.confidenceThreshold,
    showConfidence: confidenceThreshold !== null,
    superResOpacity: superResOpacity ?? SUPER_RES_DEFAULTS.superResOpacity,
    mosaickingOrder:
      mosaickingOrder ?? SETTINGS_EFFECTS_DEFAULTS.mosaickingOrder,
    upsampling: upsampling ?? SETTINGS_EFFECTS_DEFAULTS.upsampling,
    downsampling: downsampling ?? SETTINGS_EFFECTS_DEFAULTS.downsampling,
    gain: gain ?? SETTINGS_EFFECTS_DEFAULTS.gain,
    gamma: gamma ?? SETTINGS_EFFECTS_DEFAULTS.gamma,
    redRange: redRange ?? SETTINGS_EFFECTS_DEFAULTS.redRange,
    greenRange: greenRange ?? SETTINGS_EFFECTS_DEFAULTS.greenRange,
    blueRange: blueRange ?? SETTINGS_EFFECTS_DEFAULTS.blueRange,
    settingsEffectsApplies,
    serialize,
    ...setters,
  };
};

export default useVisualizationParams;
